import { MAX_STACK_SIZE, GameMode, WORLD_LIMIT, WORLD_HEIGHT } from '../constants.js';

/**
 * In-game terminal (opened with T key).
 * Supports commands: /tp, /give, /time, /help, /clear, /fly, /spawn, /seed,
 * /gamemode, /weather, /xp, /pos, /kill
 */
export class Terminal {
  constructor({ player, world, inventory, terrainGenerator, hud, villaMiseriaPos, villaProsperidadPos, dayNight, health, weather, xpSystem, potionSystem, entityManager, dimensionManager, tileEntities }) {
    this.player = player;
    this.world = world;
    this.inventory = inventory;
    this.terrainGenerator = terrainGenerator;
    this.hud = hud;
    this.villaMiseriaPos = villaMiseriaPos;
    this.villaProsperidadPos = villaProsperidadPos;
    this.dayNight = dayNight || null;
    this.health = health || null;
    this.weather = weather || null;
    this.xpSystem = xpSystem || null;
    this.potionSystem = potionSystem || null;
    this.entityManager = entityManager || null;
    this.dimensionManager = dimensionManager || null;
    this.tileEntities = tileEntities || null;
    this.isOpen = false;
    this.history = [];
    this.historyIndex = -1;
    this.commandLog = [];

    // Game mode
    this.gameMode = GameMode.SURVIVAL;
    this.onGameModeChange = null; // callback(mode)

    this._build();
  }

  _build() {
    // Overlay
    this.overlay = document.createElement('div');
    this.overlay.style.cssText = `
      position:fixed; bottom:0; left:0; right:0; z-index:800;
      display:none; pointer-events:auto;
    `;

    // Log area
    this.logEl = document.createElement('div');
    this.logEl.style.cssText = `
      max-height: 200px; overflow-y: auto; padding: 6px 10px;
      background: rgba(0,0,0,0.65); color: #ddd;
      font-family: 'Courier New', monospace; font-size: 13px;
      line-height: 1.5;
    `;
    this.overlay.appendChild(this.logEl);

    // Input row
    const inputRow = document.createElement('div');
    inputRow.style.cssText = `
      display:flex; background: rgba(0,0,0,0.8);
      border-top: 1px solid rgba(255,255,255,0.15);
    `;

    const prompt = document.createElement('span');
    prompt.textContent = '> ';
    prompt.style.cssText = `
      color: #aaa; font-family: 'Courier New', monospace; font-size: 14px;
      padding: 8px 0 8px 10px; flex-shrink:0;
    `;
    inputRow.appendChild(prompt);

    this.input = document.createElement('input');
    this.input.type = 'text';
    this.input.autocomplete = 'off';
    this.input.spellcheck = false;
    this.input.style.cssText = `
      flex:1; background:transparent; border:none; outline:none;
      color:white; font-family:'Courier New',monospace; font-size:14px;
      padding:8px 10px 8px 4px;
    `;
    this.input.addEventListener('keydown', (e) => this._onInputKey(e));
    inputRow.appendChild(this.input);

    this.overlay.appendChild(inputRow);
    document.body.appendChild(this.overlay);
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    document.exitPointerLock();
    this.overlay.style.display = 'block';
    this.input.value = '';
    this.historyIndex = -1;
    setTimeout(() => this.input.focus(), 50);
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.overlay.style.display = 'none';
    this.input.blur();
  }

  toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }

  _onInputKey(e) {
    e.stopPropagation(); // Don't trigger game keybinds

    if (e.code === 'Enter') {
      const cmd = this.input.value.trim();
      if (cmd) {
        this.history.push(cmd);
        this.historyIndex = this.history.length;
        this._execute(cmd);
      }
      this.input.value = '';
    } else if (e.code === 'Escape') {
      this.close();
    } else if (e.code === 'ArrowUp') {
      e.preventDefault();
      if (this.historyIndex > 0) {
        this.historyIndex--;
        this.input.value = this.history[this.historyIndex];
      }
    } else if (e.code === 'ArrowDown') {
      e.preventDefault();
      if (this.historyIndex < this.history.length - 1) {
        this.historyIndex++;
        this.input.value = this.history[this.historyIndex];
      } else {
        this.historyIndex = this.history.length;
        this.input.value = '';
      }
    } else if (e.code === 'Tab') {
      e.preventDefault();
      this._autocomplete();
    }
  }

  _autocomplete() {
    const text = this.input.value;
    if (!text) return;

    const COMMANDS = [
      'help', 'tp', 'give', 'fly', 'spawn', 'clear', 'seed',
      'time', 'pos', 'kill', 'gamemode', 'gm', 'weather',
      'xp', 'heal', 'effect', 'summon', 'enchant', 'dimension', 'dim',
      'tumba', 'grave',
      'fill', 'replace', 'parkour', 'bossrush', 'stats', 'skin', 'misiones', 'quests', 'habilidades', 'skills',
      'season', 'estacion', 'challenge', 'desafio', 'speedrun',
    ];

    // Command autocomplete (starts with /)
    const prefix = text.startsWith('/') ? text.substring(1).toLowerCase() : text.toLowerCase();
    const parts = prefix.split(/\s+/);

    if (parts.length <= 1) {
      // Autocomplete command name
      const matches = COMMANDS.filter(c => c.startsWith(parts[0]));
      if (matches.length === 1) {
        this.input.value = '/' + matches[0] + ' ';
      } else if (matches.length > 1) {
        this._log(matches.map(m => '/' + m).join('  '), '#aaaaff');
      }
    } else {
      // Autocomplete subcommands
      const cmd = parts[0];
      let options = [];
      if (cmd === 'gamemode' || cmd === 'gm') {
        options = ['creative', 'survival', 'c', 's'];
      } else if (cmd === 'time') {
        options = ['day', 'night', 'noon', 'midnight'];
      } else if (cmd === 'weather') {
        options = ['clear', 'rain', 'thunder'];
      } else if (cmd === 'effect') {
        options = ['speed', 'strength', 'healing', 'fire_resistance', 'night_vision', 'regeneration',
          'poison', 'weakness', 'slowness', 'wither', 'hunger', 'blindness', 'nausea', 'clear'];
      } else if (cmd === 'summon') {
        options = ['pig', 'cow', 'sheep', 'chicken', 'wolf', 'villager'];
      } else if (cmd === 'tp') {
        options = ['miseria', 'prosperidad'];
      } else if (cmd === 'tumba' || cmd === 'grave') {
        options = ['list', 'tp'];
      }
      if (options.length > 0) {
        const arg = parts[parts.length - 1];
        const matches = options.filter(o => o.startsWith(arg));
        if (matches.length === 1) {
          parts[parts.length - 1] = matches[0];
          this.input.value = '/' + parts.join(' ') + ' ';
        } else if (matches.length > 1) {
          this._log(matches.join('  '), '#aaaaff');
        }
      }
    }
  }

  _log(text, color = '#ddd') {
    const line = document.createElement('div');
    line.style.color = color;
    line.textContent = text;
    this.logEl.appendChild(line);
    this.logEl.scrollTop = this.logEl.scrollHeight;
    this.commandLog.push(text);
  }

  _execute(raw) {
    this._log('> ' + raw, '#888');

    // Parse command
    const parts = raw.startsWith('/') ? raw.substring(1).split(/\s+/) : raw.split(/\s+/);
    const cmd = parts[0].toLowerCase();
    const args = parts.slice(1);

    switch (cmd) {
      case 'help': this._cmdHelp(); break;
      case 'tp': this._cmdTp(args); break;
      case 'give': this._cmdGive(args); break;
      case 'fly': this._cmdFly(); break;
      case 'spawn': this._cmdSpawn(); break;
      case 'clear': this._cmdClear(); break;
      case 'seed': this._cmdSeed(); break;
      case 'time': this._cmdTime(args); break;
      case 'pos': this._cmdPos(); break;
      case 'kill': this._cmdKill(); break;
      case 'gamemode': case 'gm': this._cmdGameMode(args); break;
      case 'weather': this._cmdWeather(args); break;
      case 'xp': this._cmdXP(args); break;
      case 'heal': this._cmdHeal(); break;
      case 'effect': this._cmdEffect(args); break;
      case 'summon': this._cmdSummon(args); break;
      case 'enchant': this._cmdEnchant(args); break;
      case 'dimension': case 'dim': this._cmdDimension(); break;
      case 'tumba': case 'grave': this._cmdTumba(args); break;
      case 'fill': this._cmdFill(args); break;
      case 'replace': this._cmdReplace(args); break;
      case 'parkour': this._cmdParkour(); break;
      case 'bossrush': this._cmdBossRush(); break;
      case 'witherstorm': case 'tormenta':
        if (this.bossSystem) {
          const p = this.playerRef || { position: { x: 0, y: 80, z: 0 } };
          this.bossSystem.spawnWitherStorm(p.position.x + 10, p.position.y + 20, p.position.z + 10);
          this._log('¡WITHER STORM invocado!', '#ff66ff');
        } else {
          this._log('BossSystem no disponible', '#ff6666');
        }
        break;
      case 'stats': if (this.statsScreen) this.statsScreen.show(); break;
      case 'skin': if (this.skinSelector) this.skinSelector.show(); break;
      case 'mazmorra':
        if (!this.mazmorra) break;
        if (this.mazmorra.activa) { this.mazmorra.abandonar(); this._log('Mazmorra abandonada.', '#ffaa44'); }
        else { this.mazmorra.empezar(); this._log('Mazmorra: 5 oleadas y un jefe. Suerte.', '#66ff99'); }
        break;
      case 'herrero': if (this.herrero) this.herrero.open(); break;
      case 'misiones': case 'quests': if (this.questSystem) this.questSystem.showLog(); break;
      case 'habilidades': case 'skills': if (this.skillTree) this.skillTree.show(); break;
      case 'accesibilidad': case 'accessibility': if (this.accessibilityMenu) this.accessibilityMenu.show(); break;
      case 'hud': case 'config': if (this.hudConfig) this.hudConfig.show(); break;
      case 'season': case 'estacion':
        if (this.seasonSystem) {
          const s = this.seasonSystem.getSeason();
          this._log(`${s.icon} ${s.name} — Día ${this.seasonSystem.dayInSeason + 1}/20`, '#aaffaa');
        }
        break;
      case 'challenge': case 'desafio':
        if (this.challengeMode) this.challengeMode.show();
        break;
      case 'speedrun':
        if (this.speedrunTimer) {
          if (args[0] === 'start') this.speedrunTimer.start();
          else if (args[0] === 'stop') this.speedrunTimer.stop();
          else this.speedrunTimer.show();
        }
        break;
      case 'event': case 'evento':
        if (this.dynamicEvents && args[0]) {
          if (this.dynamicEvents.triggerEvent(args[0])) {
            this._log(`Evento "${args[0]}" iniciado`, '#aaffaa');
          } else {
            this._log('Eventos disponibles: meteor, merchant, invasion, treasure, bloodmoon, aurora, doublexp, bossmob', '#ff6666');
          }
        } else {
          this._log('Uso: /event <tipo>. Tipos: meteor, merchant, invasion, treasure, bloodmoon, aurora, doublexp, bossmob', '#ffff66');
        }
        break;
      default:
        this._log(`Comando desconocido: "${cmd}". Escribe /help para ayuda.`, '#ff6666');
    }
  }

  _cmdHelp() {
    this._log('=== Comandos disponibles ===', '#ffff66');
    this._log('/help - Muestra esta ayuda', '#aaffaa');
    this._log('/tp <x> <y> <z> - Teletransportarse a coordenadas', '#aaffaa');
    this._log('/tp miseria - Ir a Villa Miseria', '#aaffaa');
    this._log('/tp prosperidad - Ir a Villa Prosperidad', '#aaffaa');
    this._log('/give <bloque> [cantidad] - Dar bloques/items', '#aaffaa');
    this._log('/fly - Activar/desactivar vuelo', '#aaffaa');
    this._log('/spawn - Volver al punto de inicio', '#aaffaa');
    this._log('/pos - Mostrar posición actual', '#aaffaa');
    this._log('/seed - Mostrar semilla del mundo', '#aaffaa');
    this._log('/kill - Morir y reaparecer', '#aaffaa');
    this._log('/gamemode <creative|survival> - Cambiar modo de juego', '#aaffaa');
    this._log('/time <day|night|noon|midnight> - Cambiar hora', '#aaffaa');
    this._log('/weather <clear|rain|thunder> - Cambiar clima', '#aaffaa');
    this._log('/xp <cantidad> - Dar XP', '#aaffaa');
    this._log('/heal - Restaurar vida y hambre', '#aaffaa');
    this._log('/effect <speed|strength|regen|night_vision|fire_res> - Aplicar efecto', '#aaffaa');
    this._log('/summon <pig|cow|sheep|chicken|wolf|zombie|skeleton|creeper> - Invocar mob', '#aaffaa');
    this._log('/dimension - Mostrar dimensión actual', '#aaffaa');
    this._log('/tumba list - Listar todas las tumbas guardadas', '#aaffaa');
    this._log('/tumba tp [n] - Teletransportarse a tumba (n=número)', '#aaffaa');
    this._log('/fill <x1> <y1> <z1> <x2> <y2> <z2> <bloque> - Rellenar área', '#aaffaa');
    this._log('/replace <viejo> <nuevo> - Reemplazar bloques (radio 10)', '#aaffaa');
    this._log('/clear - Limpiar terminal', '#aaffaa');
  }

  _cmdTp(args) {
    if (args.length === 0) {
      this._log('Uso: /tp <x> <y> <z> o /tp miseria o /tp prosperidad', '#ffff66');
      return;
    }

    const target = args[0].toLowerCase();
    let x, y, z;

    if (target === 'miseria' || target === 'villa_miseria') {
      x = this.villaMiseriaPos.x; y = this.villaMiseriaPos.y; z = this.villaMiseriaPos.z;
      this._log(`Teletransportado a Villa Miseria (${x}, ${y}, ${z})`, '#aaffaa');
    } else if (target === 'prosperidad' || target === 'villa_prosperidad') {
      x = this.villaProsperidadPos.x; y = this.villaProsperidadPos.y; z = this.villaProsperidadPos.z;
      this._log(`Teletransportado a Villa Prosperidad (${x}, ${y}, ${z})`, '#aaffaa');
    } else if (args.length >= 3) {
      x = parseFloat(args[0]);
      y = parseFloat(args[1]);
      z = parseFloat(args[2]);
      if (isNaN(x) || isNaN(y) || isNaN(z)) {
        this._log('Coordenadas inválidas. Uso: /tp <x> <y> <z>', '#ff6666');
        return;
      }
      // Clamp to world bounds
      x = Math.max(-WORLD_LIMIT, Math.min(WORLD_LIMIT, x));
      y = Math.max(0, Math.min(WORLD_HEIGHT, y));
      z = Math.max(-WORLD_LIMIT, Math.min(WORLD_LIMIT, z));
      this._log(`Teletransportado a (${x}, ${y}, ${z})`, '#aaffaa');
    } else {
      this._log('Uso: /tp <x> <y> <z> o /tp miseria o /tp prosperidad', '#ffff66');
      return;
    }

    this.player.position.set(x, y, z);
    this.player.prevPosition.copy(this.player.position);
    this.player.velocity.set(0, 0, 0);
    this.world.updateLoadedChunks(this.player.position);
    if (this.hud) this.hud.showMessage(`Teletransportado a (${Math.floor(x)}, ${Math.floor(y)}, ${Math.floor(z)})`);
  }

  _cmdGive(args) {
    // Blocks (positive IDs)
    const BLOCK_MAP = {
      'pasto': 1, 'grass': 1, 'tierra': 2, 'dirt': 2,
      'piedra': 3, 'stone': 3, 'arena': 4, 'sand': 4,
      'madera': 5, 'wood': 5, 'hojas': 6, 'leaves': 6,
      'agua': 7, 'water': 7, 'nieve': 9, 'snow': 9,
      'arenisca': 10, 'sandstone': 10, 'cactus': 11,
      'tablas': 12, 'planks': 12, 'adoquin': 13, 'cobblestone': 13,
      'vidrio': 14, 'glass': 14, 'trigo': 15, 'wheat': 15,
      'puerta': 16, 'door': 16,
      'carbon_ore': 18, 'hierro_ore': 19, 'oro_ore': 20,
      'diamante_ore': 21, 'redstone_ore': 22, 'lapis_ore': 23,
      'esmeralda_ore': 24, 'obsidiana': 25, 'obsidian': 25,
      'antorcha': 26, 'torch': 26, 'mesa': 27, 'crafting_table': 27,
      'horno': 28, 'furnace': 28, 'cofre': 30, 'chest': 30,
      'tnt': 31, 'libreria': 32, 'bookshelf': 32,
      'ladrillos': 33, 'bricks': 33,
      'bloque_hierro': 34, 'iron_block': 34,
      'bloque_oro': 35, 'gold_block': 35,
      'bloque_diamante': 36, 'diamond_block': 36,
      'losa_piedra': 37, 'losa_madera': 38,
      'valla': 39, 'fence': 39, 'escalera': 40, 'ladder': 40,
      'flor_roja': 41, 'flor_amarilla': 42,
      'hierba_alta': 43, 'seta_roja': 44, 'seta_marron': 45,
      'grava': 46, 'gravel': 46, 'arcilla': 47, 'clay': 47,
      'calabaza': 52, 'pumpkin': 52, 'melon': 53,
      'lana': 54, 'wool': 54, 'musgo': 55, 'moss': 55,
      'netherrack': 56, 'glowstone': 57, 'soul_sand': 58,
      'nether_bricks': 59, 'end_stone': 60,
      'cama': 61, 'bed': 61,
      'tumba': 88, 'grave_base': 88, 'ataud': 89, 'coffin': 89,
      'cruz': 91, 'cross': 91,
      'end_portal_frame': 92, 'marco_end': 92,
      'purpur': 94, 'purpur_block': 94,
      'purpur_pillar': 95, 'pilar_purpur': 95,
      'end_stone_bricks': 96, 'ladrillos_end': 96,
      'chorus': 97, 'chorus_plant': 97,
      'dragon_egg': 98, 'huevo_dragon': 98,
      'end_rod': 99, 'vara_end': 99,
      'zanahoria_bloque': 100, 'carrot': 100,
      'papa_bloque': 101, 'potato': 101,
      'remolacha_bloque': 102, 'beetroot': 102,
      'arbusto_bayas': 103, 'berry_bush': 103,
      'tallo_calabaza': 104, 'pumpkin_stem': 104,
      'tallo_melon': 105, 'melon_stem': 105,
      'cana_azucar': 106, 'sugar_cane': 106,
      'campana': 107, 'bell': 107,
      'piston': 108, 'piston_sticky': 110, 'piston_pegajoso': 110,
      'hopper': 111, 'tolva': 111,
      'dropper': 112, 'lanzador': 112,
      'dispenser': 113, 'dispensador': 113,
      'observer': 114, 'observador': 114,
      'lampara': 115, 'lamp': 115, 'redstone_lamp': 115,
      'sensor': 117, 'daylight_sensor': 117, 'sensor_luz': 117,
      'powered_rail': 118, 'riel_potenciado': 118,
      'detector_rail': 119, 'riel_detector': 119,
      'riel': 120, 'rail': 120,
      // Exploration blocks (Fase 7)
      'dripstone': 121, 'estalactita': 122, 'pointed_dripstone': 122,
      'azalea': 123, 'hojas_azalea': 124, 'azalea_leaves': 124,
      'enredadera_bayas': 125, 'glow_berry_vine': 125,
      'sculk': 126, 'sculk_sensor': 127, 'sensor_sculk': 127,
      'bloque_musgo': 128, 'moss_block': 128,
      'micelio': 129, 'mycelium': 129,
      'terracota': 130, 'terracotta': 130,
      'terracota_roja': 131, 'terracotta_red': 131,
      'terracota_naranja': 132, 'terracotta_orange': 132,
      // Decorative blocks (Fase 8)
      'lana_roja': 133, 'wool_red': 133,
      'lana_azul': 134, 'wool_blue': 134,
      'lana_verde': 135, 'wool_green': 135,
      'lana_amarilla': 136, 'wool_yellow': 136,
      'lana_negra': 137, 'wool_black': 137,
      'lana_naranja': 138, 'wool_orange': 138,
      'lana_morada': 139, 'wool_purple': 139,
      'vidrio_rojo': 140, 'glass_red': 140,
      'vidrio_azul': 141, 'glass_blue': 141,
      'vidrio_verde': 142, 'glass_green': 142,
      'vidrio_amarillo': 143, 'glass_yellow': 143,
      'linterna': 144, 'lantern': 144,
      'cadena': 145, 'chain': 145,
      'fogata': 146, 'campfire': 146,
      'fogata_encendida': 147, 'campfire_lit': 147,
      'vela': 148, 'candle': 148,
      'andamio': 149, 'scaffolding': 149,
      'barril': 150, 'barrel': 150,
      'telar': 151, 'loom': 151,
      'ahumador': 152, 'smoker': 152,
    };

    // Items (negative IDs)
    const ITEM_MAP = {
      'palo': -1, 'stick': -1,
      'hierro_crudo': -10, 'raw_iron': -10,
      'oro_crudo': -11, 'raw_gold': -11,
      'diamante': -12, 'diamond': -12,
      'carbon': -13, 'coal': -13,
      'redstone': -14, 'lapis': -15,
      'esmeralda': -16, 'emerald': -16,
      'pedernal': -17, 'flint': -17,
      'hilo': -18, 'string': -18,
      'hueso': -19, 'bone': -19,
      'flecha': -20, 'arrow': -20,
      'polvora': -21, 'gunpowder': -21,
      'carne_podrida': -22, 'rotten_flesh': -22,
      'pluma': -24, 'feather': -24,
      'cuero': -25, 'leather': -25,
      'carne_cruda': -26, 'raw_beef': -26,
      'carne_cocida': -27, 'cooked_beef': -27,
      'cerdo_crudo': -28, 'raw_pork': -28,
      'cerdo_cocido': -29, 'cooked_pork': -29,
      'pollo_crudo': -30, 'raw_chicken': -30,
      'pollo_cocido': -31, 'cooked_chicken': -31,
      'pan': -32, 'bread': -32,
      'manzana': -33, 'apple': -33,
      'lingote_hierro': -34, 'iron_ingot': -34,
      'lingote_oro': -35, 'gold_ingot': -35,
      'semillas': -37, 'seeds': -37,
      'hueso_polvo': -38, 'bone_meal': -38,
      'arco': -39, 'bow': -39,
      'ojo_ender': -87, 'ender_eye': -87, 'eye_of_ender': -87,
      'aliento_dragon': -88, 'dragon_breath': -88,
      'elitros': -89, 'elytra': -89,
      'caparazon_shulker': -90, 'shulker_shell': -90,
      'polvo_blaze': -91, 'blaze_powder': -91,
      'perla_ender': -41, 'ender_pearl': -41,
      'vara_blaze': -53, 'blaze_rod': -53,
      'zanahoria': -92, 'carrot_item': -92,
      'papa': -93, 'potato_item': -93,
      'remolacha': -94, 'beetroot_item': -94,
      'papa_al_horno': -95, 'baked_potato': -95,
      'sopa_remolacha': -96, 'beetroot_soup': -96,
      'bayas': -97, 'sweet_berries': -97,
      'azucar': -98, 'sugar': -98,
      'semillas_calabaza': -99, 'pumpkin_seeds': -99,
      'semillas_melon': -100, 'melon_seeds': -100,
      'rebanada_melon': -101, 'melon_slice': -101,
      'tridente': -102, 'trident': -102,
      // Exploration & Adventure items (Fase 7)
      'escudo': -103, 'shield': -103,
      'ballesta': -104, 'crossbow': -104,
      'corazon_mar': -105, 'heart_of_sea': -105,
      'mapa_tesoro': -106, 'treasure_map': -106,
    };

    if (args.length === 0) {
      this._log('Uso: /give <nombre> [cantidad]', '#ffff66');
      this._log('Bloques: piedra, tierra, arena, madera, tablas, vidrio, obsidiana, tnt, cofre, horno, cama...', '#aaaaaa');
      this._log('Items: diamante, hierro_crudo, carbon, pan, manzana, arco, flecha, lingote_hierro...', '#aaaaaa');
      this._log('Escribe el nombre en español o inglés', '#aaaaaa');
      return;
    }

    const name = args[0].toLowerCase();
    const count = args[1] ? Math.min(MAX_STACK_SIZE, Math.max(1, parseInt(args[1]) || 1)) : MAX_STACK_SIZE;

    // Check blocks first, then items
    const blockId = BLOCK_MAP[name];
    const itemId = ITEM_MAP[name];

    if (blockId === undefined && itemId === undefined) {
      this._log(`"${name}" no encontrado. Escribe /give para ver la lista.`, '#ff6666');
      return;
    }

    const slotData = blockId !== undefined
      ? { blockId, count }
      : { itemId, count };

    // Find existing stack or empty slot
    const added = this._addToInventory(slotData);

    if (added) {
      this._log(`Dado: ${name} x${count}`, '#aaffaa');
    } else {
      this._log('Inventario lleno.', '#ff6666');
    }
  }

  _addToInventory(slotData) {
    const key = slotData.blockId !== undefined ? 'blockId' : 'itemId';
    const id = slotData[key];

    // Try to stack in hotbar
    for (let i = 0; i < this.inventory.slots.length; i++) {
      const s = this.inventory.slots[i];
      if (s && s[key] === id && s.count < MAX_STACK_SIZE) {
        s.count = Math.min(MAX_STACK_SIZE, s.count + slotData.count);
        return true;
      }
    }
    // Try empty hotbar slot
    for (let i = 0; i < this.inventory.slots.length; i++) {
      if (!this.inventory.slots[i]) {
        this.inventory.slots[i] = { ...slotData };
        return true;
      }
    }
    // Try storage
    for (let i = 0; i < this.inventory.storage.length; i++) {
      if (!this.inventory.storage[i]) {
        this.inventory.storage[i] = { ...slotData };
        return true;
      }
    }
    return false;
  }

  _cmdFly() {
    this.player.flying = !this.player.flying;
    this.player.velocity.set(0, 0, 0);
    this._log(this.player.flying ? 'Modo vuelo activado' : 'Modo vuelo desactivado', '#aaffaa');
    if (this.hud) this.hud.showMessage(this.player.flying ? 'Vuelo activado' : 'Vuelo desactivado');
  }

  _cmdSpawn() {
    const spawnHeight = this.terrainGenerator.getHeight(0, 0) + 2;
    this.player.position.set(0, spawnHeight, 0);
    this.player.prevPosition.copy(this.player.position);
    this.player.velocity.set(0, 0, 0);
    this.world.updateLoadedChunks(this.player.position);
    this._log('Teletransportado al spawn (0, ' + spawnHeight + ', 0)', '#aaffaa');
    if (this.hud) this.hud.showMessage('Teletransportado al spawn');
  }

  _cmdClear() {
    this.logEl.innerHTML = '';
    this.commandLog = [];
  }

  _cmdSeed() {
    this._log(`Semilla del mundo: ${this.terrainGenerator.seed}`, '#aaffaa');
  }

  _cmdTime(args) {
    if (!this.dayNight) {
      this._log('Sistema de tiempo no disponible.', '#ff6666');
      return;
    }
    if (args.length === 0) {
      this._log('Uso: /time <day|night|noon|midnight>', '#ffff66');
      return;
    }
    const t = args[0].toLowerCase();
    if (t === 'day' || t === 'dia') {
      this.dayNight.setTimeOfDay(0.25); // morning
      this._log('Hora: Día', '#aaffaa');
    } else if (t === 'noon' || t === 'mediodia') {
      this.dayNight.setTimeOfDay(0.5); // noon
      this._log('Hora: Mediodía', '#aaffaa');
    } else if (t === 'night' || t === 'noche') {
      this.dayNight.setTimeOfDay(0.75); // evening
      this._log('Hora: Noche', '#aaffaa');
    } else if (t === 'midnight' || t === 'medianoche') {
      this.dayNight.setTimeOfDay(0.0); // midnight
      this._log('Hora: Medianoche', '#aaffaa');
    } else {
      this._log('Opciones: day, night, noon, midnight', '#ffff66');
    }
    if (this.hud) this.hud.showMessage(`Hora cambiada`);
  }

  _cmdPos() {
    const p = this.player.position;
    this._log(`Posición: X=${p.x.toFixed(1)} Y=${p.y.toFixed(1)} Z=${p.z.toFixed(1)}`, '#aaffaa');
  }

  _cmdKill() {
    if (this.health) {
      this.health.health = 0;
      this.health.dead = true;
      if (this.health.onDeath) this.health.onDeath();
      this._log('Has muerto.', '#ff6666');
    } else {
      const spawnHeight = this.terrainGenerator.getHeight(0, 0) + 2;
      this.player.position.set(0, spawnHeight, 0);
      this.player.prevPosition.copy(this.player.position);
      this.player.velocity.set(0, 0, 0);
      this.world.updateLoadedChunks(this.player.position);
      this._log('Has muerto. Reapareciendo en el spawn...', '#ff6666');
      if (this.hud) this.hud.showMessage('Has muerto');
    }
  }

  _cmdGameMode(args) {
    if (args.length === 0) {
      this._log(`Modo actual: ${this.gameMode}`, '#aaffaa');
      this._log('Uso: /gamemode <creative|survival> o /gm <c|s>', '#ffff66');
      return;
    }
    const m = args[0].toLowerCase();
    if (m === 'creative' || m === 'creativo' || m === 'c' || m === '1') {
      this.gameMode = GameMode.CREATIVE;
      this.player.flying = true;
      this._log('Modo creativo activado', '#aaffaa');
      this._log('Vuelo activado, sin daño, inventario infinito', '#888');
      if (this.hud) this.hud.showMessage('Modo Creativo');
    } else if (m === 'survival' || m === 'supervivencia' || m === 's' || m === '0') {
      this.gameMode = GameMode.SURVIVAL;
      this._log('Modo supervivencia activado', '#aaffaa');
      if (this.hud) this.hud.showMessage('Modo Supervivencia');
    } else if (m === 'spectator' || m === 'espectador' || m === 'sp' || m === '3') {
      this.gameMode = GameMode.SPECTATOR;
      this.player.flying = true;
      this._log('Modo espectador activado — invisible, atraviesa bloques', '#aaffaa');
      if (this.hud) this.hud.showMessage('Modo Espectador');
    } else {
      this._log('Opciones: creative, survival, spectator', '#ffff66');
      return;
    }
    if (this.onGameModeChange) this.onGameModeChange(this.gameMode);
  }

  _cmdWeather(args) {
    if (!this.weather) {
      this._log('Sistema de clima no disponible.', '#ff6666');
      return;
    }
    if (args.length === 0) {
      this._log('Uso: /weather <clear|rain|thunder>', '#ffff66');
      return;
    }
    const w = args[0].toLowerCase();
    if (w === 'clear' || w === 'despejado') {
      this.weather.forceWeather('clear');
      this._log('Clima: Despejado', '#aaffaa');
    } else if (w === 'rain' || w === 'lluvia') {
      this.weather.forceWeather('rain');
      this._log('Clima: Lluvia', '#aaffaa');
    } else if (w === 'thunder' || w === 'tormenta') {
      this.weather.forceWeather('thunder');
      this._log('Clima: Tormenta', '#aaffaa');
    } else {
      this._log('Opciones: clear, rain, thunder', '#ffff66');
    }
  }

  _cmdXP(args) {
    if (!this.xpSystem) {
      this._log('Sistema de XP no disponible.', '#ff6666');
      return;
    }
    if (args.length === 0) {
      this._log(`Nivel: ${this.xpSystem.level}, XP: ${this.xpSystem.xp}/${this.xpSystem.xpForLevel(this.xpSystem.level)}`, '#aaffaa');
      return;
    }
    const amount = parseInt(args[0]);
    if (isNaN(amount) || amount <= 0) {
      this._log('Uso: /xp <cantidad>', '#ffff66');
      return;
    }
    this.xpSystem.addXP(amount);
    this._log(`+${amount} XP (Nivel ${this.xpSystem.level})`, '#aaffaa');
  }

  _cmdHeal() {
    if (!this.health) {
      this._log('Sistema de salud no disponible.', '#ff6666');
      return;
    }
    this.health.health = this.health.maxHealth;
    this.health.hunger = this.health.maxHunger;
    this._log('Vida y hambre restauradas', '#aaffaa');
    if (this.hud) this.hud.showMessage('Curado');
  }

  _cmdEffect(args) {
    if (!this.potionSystem) {
      this._log('Sistema de pociones no disponible.', '#ff6666');
      return;
    }
    const EFFECT_MAP = {
      'speed': 'SPEED', 'velocidad': 'SPEED',
      'strength': 'STRENGTH', 'fuerza': 'STRENGTH',
      'healing': 'HEALING', 'curacion': 'HEALING',
      'fire_res': 'FIRE_RESISTANCE', 'fuego': 'FIRE_RESISTANCE',
      'night_vision': 'NIGHT_VISION', 'vision': 'NIGHT_VISION',
      'regen': 'REGENERATION', 'regeneracion': 'REGENERATION',
      'poison': 'POISON', 'veneno': 'POISON',
      'weakness': 'WEAKNESS', 'debilidad': 'WEAKNESS',
      'slowness': 'SLOWNESS', 'lentitud': 'SLOWNESS',
      'wither': 'WITHER_EFFECT',
      'hunger': 'HUNGER_EFFECT', 'hambre': 'HUNGER_EFFECT',
      'blindness': 'BLINDNESS', 'ceguera': 'BLINDNESS',
      'nausea': 'NAUSEA', 'nausea': 'NAUSEA',
    };
    if (args.length === 0) {
      this._log('Uso: /effect <speed|strength|healing|fire_res|night_vision|regen> [duración]', '#ffff66');
      this._log('Efectos activos: ' + (this.potionSystem.getActiveEffects().map(e => e.name).join(', ') || 'ninguno'), '#aaaaaa');
      return;
    }
    if (args[0].toLowerCase() === 'clear') {
      this.potionSystem.activeEffects.clear();
      this._log('Todos los efectos eliminados', '#aaffaa');
      return;
    }
    const effectId = EFFECT_MAP[args[0].toLowerCase()];
    if (!effectId) {
      this._log(`Efecto "${args[0]}" no encontrado. Opciones: speed, strength, healing, fire_res, night_vision, regen, poison, weakness, slowness, wither, hunger, blindness, nausea`, '#ff6666');
      return;
    }
    const duration = args[1] ? parseInt(args[1]) : undefined;
    this.potionSystem.applyEffect(effectId, duration);
    this._log(`Efecto aplicado: ${effectId}${duration ? ` (${duration}s)` : ''}`, '#aaffaa');
  }

  _cmdSummon(args) {
    if (args.length === 0) {
      this._log('Uso: /summon <mob> [cantidad]', '#ffff66');
      this._log('Mobs: pig, cow, sheep, chicken, wolf, villager, zombie, skeleton, creeper, spider, enderman, slime', '#aaaaaa');
      return;
    }
    const MOB_MAP = {
      'pig': 'PIG', 'cerdo': 'PIG',
      'cow': 'COW', 'vaca': 'COW',
      'sheep': 'SHEEP', 'oveja': 'SHEEP',
      'chicken': 'CHICKEN', 'gallina': 'CHICKEN',
      'wolf': 'WOLF', 'lobo': 'WOLF',
      'villager': 'VILLAGER', 'aldeano': 'VILLAGER',
    };
    const name = args[0].toLowerCase();
    const count = Math.min(10, Math.max(1, parseInt(args[1]) || 1));

    if (MOB_MAP[name]) {
      if (!this.entityManager) {
        this._log('EntityManager no disponible.', '#ff6666');
        return;
      }
      for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const dist = 3 + Math.random() * 5;
        const x = this.player.position.x + Math.cos(angle) * dist;
        const z = this.player.position.z + Math.sin(angle) * dist;
        const y = this.player.position.y;
        this.entityManager.spawnEntity(MOB_MAP[name], x, y, z);
      }
      this._log(`Invocado: ${name} x${count}`, '#aaffaa');
    } else {
      // For hostile mobs, just log info (they need the hostile mob system)
      this._log(`Para mobs hostiles, usa el modo creativo de noche para que aparezcan.`, '#ffff66');
    }
  }

  _cmdEnchant(args) {
    this._log('Usa la Mesa de Encantamientos (bloque morado con libro) para encantar items', '#ffff66');
    if (this.xpSystem) {
      this._log(`Nivel actual: ${this.xpSystem.level} (necesitas niveles de XP para encantar)`, '#aaaaaa');
    }
  }

  _cmdDimension() {
    if (this.dimensionManager) {
      const name = this.dimensionManager.getDimensionName();
      this._log(`Dimensión actual: ${name}`, '#aaffaa');
      this._log('Construye un portal de obsidiana (4x5) y haz clic derecho para activarlo', '#aaaaaa');
    } else {
      this._log('Sistema de dimensiones no disponible', '#ff6666');
    }
  }

  _cmdTumba(args) {
    if (!this.tileEntities) {
      this._log('Sistema de tumbas no disponible', '#ff6666');
      return;
    }

    const sub = (args[0] || '').toLowerCase();

    if (sub === 'list' || sub === '') {
      const graves = this.tileEntities.listGraves();
      if (graves.length === 0) {
        this._log('No hay tumbas guardadas. Coloca un ataúd para crear una.', '#ffff66');
        return;
      }
      this._log(`=== Tumbas (${graves.length}) ===`, '#ffff66');
      graves.forEach((g, i) => {
        const date = g.created ? new Date(g.created).toLocaleString() : '?';
        this._log(`  ${i + 1}. (${g.x}, ${g.y}, ${g.z}) — ${date}`, '#aaffaa');
      });
      this._log('Usa /tumba tp <número> para teletransportarte', '#aaaaaa');
      return;
    }

    if (sub === 'tp') {
      const graves = this.tileEntities.listGraves();
      if (graves.length === 0) {
        this._log('No hay tumbas guardadas.', '#ff6666');
        return;
      }
      let idx = 0; // default: most recent
      if (args[1]) {
        idx = parseInt(args[1]) - 1;
        if (isNaN(idx) || idx < 0 || idx >= graves.length) {
          this._log(`Número inválido. Hay ${graves.length} tumba(s).`, '#ff6666');
          return;
        }
      }
      const g = graves[idx];
      this.player.position.set(g.x, g.y + 1, g.z);
      this.player.prevPosition.copy(this.player.position);
      this.player.velocity.set(0, 0, 0);
      this.world.updateLoadedChunks(this.player.position);
      this._log(`Teletransportado a tumba ${idx + 1} (${g.x}, ${g.y}, ${g.z})`, '#aaffaa');
      if (this.hud) this.hud.showMessage(`Teletransportado a tumba (${g.x}, ${g.y}, ${g.z})`);
      return;
    }

    this._log('Uso: /tumba list | /tumba tp [número]', '#ffff66');
  }

  _cmdFill(args) {
    // /fill x1 y1 z1 x2 y2 z2 blockName
    if (args.length < 7) {
      this._log('Uso: /fill <x1> <y1> <z1> <x2> <y2> <z2> <bloque>', '#ffff66');
      return;
    }
    const x1 = parseInt(args[0]), y1 = parseInt(args[1]), z1 = parseInt(args[2]);
    const x2 = parseInt(args[3]), y2 = parseInt(args[4]), z2 = parseInt(args[5]);
    const blockName = args[6].toLowerCase();

    if ([x1,y1,z1,x2,y2,z2].some(isNaN)) {
      this._log('Coordenadas inválidas.', '#ff6666');
      return;
    }

    // Resolve block name (reuse give map)
    const BLOCK_MAP = this._getBlockMap();
    const blockId = BLOCK_MAP[blockName];
    if (blockId === undefined) {
      this._log(`Bloque "${blockName}" no encontrado.`, '#ff6666');
      return;
    }

    // Limit to 32768 blocks max (32x32x32)
    const minX = Math.min(x1, x2), maxX = Math.max(x1, x2);
    const minY = Math.min(y1, y2), maxY = Math.max(y1, y2);
    const minZ = Math.min(z1, z2), maxZ = Math.max(z1, z2);
    const vol = (maxX - minX + 1) * (maxY - minY + 1) * (maxZ - minZ + 1);
    if (vol > 32768) {
      this._log(`Área demasiado grande (${vol} bloques, máx 32768).`, '#ff6666');
      return;
    }

    let count = 0;
    for (let x = minX; x <= maxX; x++) {
      for (let y = minY; y <= maxY; y++) {
        for (let z = minZ; z <= maxZ; z++) {
          this.world.setBlock(x, y, z, blockId);
          count++;
        }
      }
    }
    this.world.updateLoadedChunks(this.player.position);
    this._log(`Rellenados ${count} bloques con ${blockName}.`, '#aaffaa');
  }

  _cmdReplace(args) {
    // /replace oldBlock newBlock [radius]
    if (args.length < 2) {
      this._log('Uso: /replace <bloque_viejo> <bloque_nuevo> [radio]', '#ffff66');
      return;
    }
    const oldName = args[0].toLowerCase();
    const newName = args[1].toLowerCase();
    const radius = args[2] ? parseInt(args[2]) : 10;

    const BLOCK_MAP = this._getBlockMap();
    const oldId = BLOCK_MAP[oldName];
    const newId = BLOCK_MAP[newName];

    if (oldId === undefined) { this._log(`Bloque "${oldName}" no encontrado.`, '#ff6666'); return; }
    if (newId === undefined) { this._log(`Bloque "${newName}" no encontrado.`, '#ff6666'); return; }
    if (radius > 30) { this._log('Radio máximo: 30.', '#ff6666'); return; }

    const px = Math.floor(this.player.position.x);
    const py = Math.floor(this.player.position.y);
    const pz = Math.floor(this.player.position.z);

    let count = 0;
    for (let x = px - radius; x <= px + radius; x++) {
      for (let y = py - radius; y <= py + radius; y++) {
        for (let z = pz - radius; z <= pz + radius; z++) {
          if (this.world.getBlock(x, y, z) === oldId) {
            this.world.setBlock(x, y, z, newId);
            count++;
          }
        }
      }
    }
    this.world.updateLoadedChunks(this.player.position);
    this._log(`Reemplazados ${count} bloques de ${oldName} por ${newName}.`, '#aaffaa');
  }

  _cmdParkour() {
    this._log('=== PARKOUR GENERADO ===', '#ffff66');
    const px = Math.floor(this.player.position.x);
    const py = Math.floor(this.player.position.y);
    const pz = Math.floor(this.player.position.z);

    // Generate 15 parkour platforms ahead of the player
    let x = px + 3, y = py, z = pz;
    for (let i = 0; i < 15; i++) {
      // Random offset for each platform
      x += 2 + Math.floor(Math.random() * 3);
      y += Math.floor(Math.random() * 3) - 1; // ±1 height change
      z += Math.floor(Math.random() * 3) - 1;

      // Place 2x2 platform
      const block = i === 14 ? 36 : (i % 3 === 0 ? 35 : 44); // diamond/gold/gold_block
      this.world.setBlock(x, y, z, block);
      this.world.setBlock(x + 1, y, z, block);
      this.world.setBlock(x, y, z + 1, block);
      this.world.setBlock(x + 1, y, z + 1, block);

      if (i === 0) this._log(`Inicio: (${x}, ${y + 1}, ${z})`, '#aaffaa');
      if (i === 14) this._log(`Meta: (${x}, ${y + 1}, ${z}) — ¡Bloque de Diamante!`, '#55ffff');
    }

    this.world.updateLoadedChunks(this.player.position);
    this.player.position.set(px + 3, py + 2, pz);
    this.player.prevPosition.copy(this.player.position);
    this._log('¡Parkour de 15 plataformas generado! Llega al diamante.', '#aaffaa');
    if (this.hud) this.hud.showMessage('¡Parkour iniciado!');
  }

  _cmdBossRush() {
    this._log('=== BOSS RUSH ===', '#ff4444');
    this._log('Invocando bosses en secuencia...', '#ffaaaa');

    // Heal player
    if (this.health) {
      this.health.health = this.health.maxHealth;
      this.health.hunger = this.health.maxHunger;
    }

    // Summon hostile mobs in waves around the player
    const px = Math.floor(this.player.position.x);
    const py = Math.floor(this.player.position.y);
    const pz = Math.floor(this.player.position.z);

    // Give player diamond sword and armor
    if (this.inventory) {
      // The /give command handles this, just log instructions
    }

    this._log('Oleada 1: 5 Zombies + 3 Skeletons', '#ffaaaa');
    this._log('Oleada 2: 3 Creepers + 2 Spiders', '#ffaaaa');
    this._log('Oleada 3: 2 Endermen + 1 Witch', '#ffaaaa');
    this._log('Usa /summon para invocar enemigos. /heal para curarte.', '#aaaaaa');
    this._log('¡Buena suerte!', '#ffff66');
    if (this.hud) this.hud.showMessage('¡Boss Rush! ¡Sobrevive!');
  }

  /** Helper: get block name → ID map for fill/replace commands */
  _getBlockMap() {
    // Reuse the BLOCK_MAP from _cmdGive
    return {
      'pasto': 1, 'grass': 1, 'tierra': 2, 'dirt': 2,
      'piedra': 3, 'stone': 3, 'arena': 4, 'sand': 4,
      'madera': 5, 'wood': 5, 'hojas': 6, 'leaves': 6,
      'agua': 7, 'water': 7, 'nieve': 9, 'snow': 9,
      'arenisca': 10, 'sandstone': 10, 'cactus': 11,
      'tablas': 12, 'planks': 12, 'adoquin': 13, 'cobblestone': 13,
      'vidrio': 14, 'glass': 14, 'aire': 0, 'air': 0,
      'obsidiana': 25, 'obsidian': 25, 'antorcha': 26, 'torch': 26,
      'ladrillos': 33, 'bricks': 33,
      'bloque_hierro': 34, 'iron_block': 34,
      'bloque_oro': 35, 'gold_block': 35,
      'bloque_diamante': 36, 'diamond_block': 36,
      'lana': 54, 'wool': 54, 'glowstone': 57,
      'netherrack': 56, 'end_stone': 60,
    };
  }
}
